import { memo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  LOUNGE_SURVEY_QUESTIONS,
  QuestionPurpose,
  SurveyQuestion,
} from '../../constants/loungeQuestions';
import SurveyQuestionCard from './SurveyQuestionCard';

interface SurveyOptionalQuestionsProps {
  answers: Map<number, number | null>;
  onAnswerChange: (questionId: number, value: number | null) => void;
  disabled?: boolean;
}

// Pre-filter questions at module level since they never change
const OPTIONAL_QUESTIONS = LOUNGE_SURVEY_QUESTIONS.filter(
  (q) => q.purpose === QuestionPurpose.GENERAL,
);

// Static sx objects moved outside component to prevent recreation
const accordionSx = {
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  border: 'none',
  borderRadius: { xs: 2, sm: 3 },
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  '&:before': {
    display: 'none',
  },
  mb: { xs: 2, sm: 3 },
  // Removed transition and hover effects that cause repaints during scroll
} as const;

const accordionSummarySx = {
  minHeight: { xs: 56, sm: 72, md: 80 },
  '& .MuiAccordionSummary-content': {
    my: { xs: 1.5, sm: 2.5 },
  },
  px: { xs: 2, sm: 4, md: 5 },
} as const;

const expandIconSx = {
  color: 'primary.main',
  fontSize: { xs: '1.5rem', sm: '2rem' },
} as const;

const titleSx = {
  fontSize: {
    xs: '0.95rem',
    sm: '1.25rem',
    md: '1.3rem',
    lg: '1.2rem',
  },
  fontWeight: 600,
  color: 'text.primary',
  lineHeight: 1.5,
} as const;

const detailsSx = {
  px: { xs: 1, sm: 3, md: 4 },
  pb: { xs: 2, sm: 4, md: 5 },
} as const;

// Memoized question item to prevent re-renders when other questions change
const MemoizedQuestionItem = memo(function MemoizedQuestionItem({
  question,
  value,
  onAnswerChange,
  disabled,
}: {
  question: SurveyQuestion;
  value: number | null;
  onAnswerChange: (questionId: number, value: number | null) => void;
  disabled: boolean;
}) {
  return (
    <SurveyQuestionCard
      question={question}
      value={value}
      onChange={onAnswerChange}
      disabled={disabled}
    />
  );
});

const SurveyOptionalQuestions = memo(function SurveyOptionalQuestions({
  answers,
  onAnswerChange,
  disabled = false,
}: SurveyOptionalQuestionsProps) {
  return (
    <Accordion variant="outlined" defaultExpanded={false} sx={accordionSx}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={expandIconSx} />}
        aria-controls="optional-questions-content"
        id="optional-questions-header"
        sx={accordionSummarySx}
      >
        <Typography sx={titleSx}>
          Click here to share more about your experience 😊
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={detailsSx}>
        {OPTIONAL_QUESTIONS.map((question) => (
          <MemoizedQuestionItem
            key={question.id}
            question={question}
            value={answers.get(question.id) ?? null}
            onAnswerChange={onAnswerChange}
            disabled={disabled}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
});

export default SurveyOptionalQuestions;
